import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useHomeStore, Product, Service, Vacancy } from '@/stores/useHomeStore';
import PropertyCard from '@/components/cards/PropertyCard';
import ProductCard from '@/components/cards/ProductCard';
import ServiceCard from '@/components/cards/ServiceCard';
import {
  MapPin, Heart, Bell, ChevronRight, Zap, Wrench,
  Home, Bed, Star, AlertCircle, RefreshCw,
  TrendingUp, Search, Navigation, Loader2, X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/config/supabase';
import { getImageUrl, extractImageUrls } from '@/config/media';

// ─── helpers ─────────────────────────────────────────────────────────────────

const getPropertyImage = (p: Vacancy) =>
  extractImageUrls(p.images)[0] || 'https://placehold.co/400x300/1a1a1a/ffc800?text=Property';

const getProductImage = (p: Product) => {
  const urls = extractImageUrls(p.media_urls || p.image_url);
  return urls[0] || 'https://placehold.co/400x400/1a1a1a/ffc800?text=Product';
};

const getServiceImage = (s: Service) => {
  const urls = extractImageUrls(s.media_urls);
  return urls[0] || '';
};

// Check if a location string contains any of the area keywords
const matchesArea = (locationStr: string, areaKeywords: string[]): boolean => {
  if (!locationStr || areaKeywords.length === 0) return true;
  const loc = locationStr.toLowerCase();
  return areaKeywords.some(kw => loc.includes(kw.toLowerCase()));
};

// ─── Section ─────────────────────────────────────────────────────────────────

interface SectionProps<T> {
  title: string;
  icon: React.ReactNode;
  items: T[];
  loading: boolean;
  renderItem: (item: T, i: number) => React.ReactNode;
  onSeeAll?: () => void;
  emptyMessage?: string;
  badge?: string;
}

function Section<T>({ title, icon, items, loading, renderItem, onSeeAll, emptyMessage, badge }: SectionProps<T>) {
  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-primary dark:text-golden">{icon}</span>
          <h2 className="text-base font-bold font-display text-light-text dark:text-dark-text">{title}</h2>
          {!loading && items.length > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 dark:bg-golden/10 text-primary dark:text-golden font-semibold">
              {items.length}
            </span>
          )}
          {badge && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-golden/20 text-golden font-bold uppercase tracking-wide">
              {badge}
            </span>
          )}
        </div>
        {onSeeAll && (
          <button
            onClick={onSeeAll}
            className="flex items-center gap-0.5 text-sm font-semibold text-primary dark:text-golden hover:opacity-75 transition-opacity"
          >
            See All <ChevronRight size={15} />
          </button>
        )}
      </div>

      <div className="overflow-x-auto pb-3 scrollbar-hide">
        {loading ? (
          <div className="flex gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex-shrink-0 w-56 h-52 rounded-xl bg-light-card dark:bg-dark-card animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-light-subtle dark:text-dark-subtle py-4">
            {emptyMessage || 'Nothing here yet.'}
          </p>
        ) : (
          <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
            {items.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05, duration: 0.3 }}
                className="flex-shrink-0 w-56"
              >
                {renderItem(item, i)}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Nearby banner ────────────────────────────────────────────────────────────

interface NearbyBannerProps {
  area: string | null;
  loading: boolean;
  error: string | null;
  onRequest: () => void;
  onClear: () => void;
}

const NearbyBanner: React.FC<NearbyBannerProps> = ({ area, loading, error, onRequest, onClear }) => {
  if (area) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/10 dark:bg-golden/10 border border-primary/20 dark:border-golden/20"
      >
        <div className="w-8 h-8 rounded-full bg-primary/20 dark:bg-golden/20 flex items-center justify-center flex-shrink-0">
          <Navigation size={14} className="text-primary dark:text-golden" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-primary dark:text-golden">Showing results near you</p>
          <p className="text-xs text-light-subtle dark:text-dark-subtle truncate">{area}</p>
        </div>
        <button onClick={onClear} className="p-1 rounded-full hover:bg-primary/10 dark:hover:bg-golden/10 transition-colors">
          <X size={14} className="text-light-subtle dark:text-dark-subtle" />
        </button>
      </motion.div>
    );
  }

  if (error) {
    return (
      <div className="mb-6 flex items-center gap-3 px-4 py-3 rounded-xl bg-danger/10 border border-danger/20">
        <AlertCircle size={14} className="text-danger flex-shrink-0" />
        <p className="text-xs text-danger flex-1">{error}</p>
        <button onClick={onRequest} className="text-xs font-semibold text-primary dark:text-golden">Retry</button>
      </div>
    );
  }

  return (
    <div className="mb-6 flex items-center gap-3 px-4 py-3 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border">
      <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-golden/10 flex items-center justify-center flex-shrink-0">
        {loading
          ? <Loader2 size={14} className="text-primary dark:text-golden animate-spin" />
          : <MapPin size={14} className="text-primary dark:text-golden" />
        }
      </div>
      <div className="flex-1">
        <p className="text-xs font-semibold text-light-text dark:text-dark-text">
          {loading ? 'Getting your location…' : 'Find services & properties near you'}
        </p>
        {!loading && (
          <p className="text-xs text-light-subtle dark:text-dark-subtle">Tap to filter by your area</p>
        )}
      </div>
      {!loading && (
        <button
          onClick={onRequest}
          className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-primary dark:bg-golden text-white dark:text-dark-bg text-xs font-bold hover:opacity-90 transition-opacity"
        >
          Use My Location
        </button>
      )}
    </div>
  );
};

// ─── Hero ─────────────────────────────────────────────────────────────────────

const Hero: React.FC<{ userName?: string; onSearch: () => void }> = ({ userName, onSearch }) => (
  <div className="relative mb-6 rounded-2xl overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/90 to-golden/60 dark:from-dark-card dark:via-dark-card dark:to-golden/20" />
    <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-golden/20 blur-2xl" />
    <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-white/10 blur-xl" />

    <div className="relative px-6 py-8">
      <p className="text-white/70 text-sm font-medium mb-1">
        {userName ? `Welcome back, ${userName.split(' ')[0]} 👋` : 'Discover your next home 🏡'}
      </p>
      <h1 className="text-2xl font-bold font-display text-white mb-1 leading-tight">
        Properties & Services<br />in Kenya
      </h1>
      <p className="text-white/60 text-sm mb-5">
        Thousands of listings across Kenya
      </p>

      <button
        onClick={onSearch}
        className="flex items-center gap-3 w-full max-w-sm bg-white/15 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3 text-white/80 text-sm hover:bg-white/20 transition-colors"
      >
        <Search size={16} />
        <span>Search properties, services…</span>
      </button>

      <div className="flex items-center gap-5 mt-5">
        {[
          { label: 'Properties', value: '1.2K+' },
          { label: 'Services',   value: '800+' },
          { label: 'Counties',   value: '47' },
        ].map((s) => (
          <div key={s.label}>
            <p className="text-white font-bold font-display text-lg leading-none">{s.value}</p>
            <p className="text-white/60 text-xs mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── Category pills ───────────────────────────────────────────────────────────

const CATEGORIES = [
  { label: 'All',        icon: '✨' },
  { label: 'Apartments', icon: '🏢' },
  { label: 'Houses',     icon: '🏠' },
  { label: 'Studios',    icon: '🛋️' },
  { label: 'Villas',     icon: '🏡' },
  { label: 'Cleaning',   icon: '🧹' },
  { label: 'Repair',     icon: '🔧' },
  { label: 'Beauty',     icon: '💇' },
  { label: 'Consulting', icon: '💼' },
];

const CategoryPills: React.FC<{ active: string; onChange: (v: string) => void }> = ({ active, onChange }) => (
  <div className="overflow-x-auto scrollbar-hide mb-8">
    <div className="flex gap-2" style={{ minWidth: 'max-content' }}>
      {CATEGORIES.map((c) => (
        <button
          key={c.label}
          onClick={() => onChange(c.label)}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
            active === c.label
              ? 'bg-primary dark:bg-golden text-white dark:text-dark-bg shadow-sm'
              : 'bg-light-card dark:bg-dark-card text-light-subtle dark:text-dark-subtle border border-light-border dark:border-dark-border hover:border-primary dark:hover:border-golden'
          }`}
        >
          <span className="text-base leading-none">{c.icon}</span>
          {c.label}
        </button>
      ))}
    </div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');

  // Geolocation state
  const [userArea, setUserArea] = useState<string | null>(null);
  const [areaKeywords, setAreaKeywords] = useState<string[]>([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  const {
    featuredVacancies, featuredProducts, featuredServices,
    vacanciesLoading, productsLoading, servicesLoading,
    error, fetchHomeData, clearErrors,
  } = useHomeStore();

  useEffect(() => { fetchHomeData(); return () => clearErrors(); }, []);

  useEffect(() => {
    if (!user) return;
    supabase.from('user_favorites').select('item_id').eq('user_id', user.id)
      .then(({ data }) => { if (data) setFavorites(new Set(data.map((f) => f.item_id))); });
  }, [user]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchHomeData();
    setRefreshing(false);
  }, [fetchHomeData]);

  const handleToggleFavorite = async (id: string, type: 'property' | 'product' | 'service') => {
    if (!user) { navigate('/login'); return; }
    const isFav = favorites.has(id);
    setFavorites((prev) => { const n = new Set(prev); isFav ? n.delete(id) : n.add(id); return n; });
    if (isFav) {
      await supabase.from('user_favorites').delete().eq('user_id', user.id).eq('item_type', type).eq('item_id', id);
    } else {
      await supabase.from('user_favorites').insert({ user_id: user.id, item_type: type, item_id: id });
    }
  };

  // ── Geolocation ──────────────────────────────────────────────────────────────
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by your browser.');
      return;
    }
    setGeoLoading(true);
    setGeoError(null);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          // Reverse geocode using Nominatim (free, no API key)
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
            { headers: { 'Accept-Language': 'en' } }
          );
          const data = await res.json();
          const addr = data.address || {};

          // Build area keywords from most specific to least
          const keywords: string[] = [];
          if (addr.suburb)       keywords.push(addr.suburb);
          if (addr.neighbourhood) keywords.push(addr.neighbourhood);
          if (addr.city_district) keywords.push(addr.city_district);
          if (addr.town)         keywords.push(addr.town);
          if (addr.city)         keywords.push(addr.city);
          if (addr.county)       keywords.push(addr.county);
          if (addr.state)        keywords.push(addr.state);

          // Display label — use suburb + city or county
          const displayParts = [
            addr.suburb || addr.neighbourhood || addr.city_district,
            addr.city || addr.town || addr.county,
          ].filter(Boolean);
          const displayArea = displayParts.join(', ') || data.display_name?.split(',').slice(0, 2).join(',') || 'Your area';

          setAreaKeywords(keywords.filter(Boolean));
          setUserArea(displayArea);
        } catch {
          // Fallback: just use coordinates label
          setUserArea(`${latitude.toFixed(3)}°N, ${longitude.toFixed(3)}°E`);
          setAreaKeywords([]);
        } finally {
          setGeoLoading(false);
        }
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === 1) setGeoError('Location access denied. Please allow location in your browser.');
        else if (err.code === 2) setGeoError('Could not determine your location. Try again.');
        else setGeoError('Location request timed out. Try again.');
      },
      { timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  const clearLocation = useCallback(() => {
    setUserArea(null);
    setAreaKeywords([]);
    setGeoError(null);
  }, []);

  // ── Filtering ────────────────────────────────────────────────────────────────

  // Category filter maps
  const propertyTypeMap: Record<string, string[]> = {
    Apartments: ['apartment'], Houses: ['house'], Studios: ['studio'], Villas: ['villa'],
  };
  const serviceCatMap: Record<string, string[]> = {
    Cleaning: ['cleaning'], Repair: ['repair'], Beauty: ['beauty'], Consulting: ['consulting'],
  };

  const filterVacancies = (items: Vacancy[]) => {
    let result = items;
    // Category filter
    if (activeCategory !== 'All' && propertyTypeMap[activeCategory]) {
      result = result.filter(v => propertyTypeMap[activeCategory].includes((v.type || '').toLowerCase()));
    }
    // Nearby filter
    if (areaKeywords.length > 0) {
      result = result.filter(v => matchesArea(v.location, areaKeywords));
    }
    return result;
  };

  const filterServices = (items: Service[]) => {
    let result = items;
    // Category filter
    if (activeCategory !== 'All' && serviceCatMap[activeCategory]) {
      result = result.filter(s => serviceCatMap[activeCategory].some(k => s.category?.toLowerCase().includes(k)));
    }
    // Nearby filter — services have business_name but no location field in the type
    // We match on any string field that might contain location info
    if (areaKeywords.length > 0) {
      result = result.filter(s => {
        const searchStr = [s.business_name, s.description, s.category].join(' ');
        return matchesArea(searchStr, areaKeywords);
      });
    }
    return result;
  };

  const filteredVacancies = filterVacancies(featuredVacancies);
  const filteredServices  = filterServices(featuredServices);
  const nearbyActive      = areaKeywords.length > 0;

  // ── Formatters ───────────────────────────────────────────────────────────────

  const formatProduct = (p: Product) => ({
    id: p.id, name: p.name, description: p.description, price: p.price,
    category: p.category, images: [getProductImage(p)], business_id: p.business_id,
    business_name: p.business_name, stock_quantity: p.quantity_in_stock,
    rating: p.rating, is_featured: p.is_featured, is_active: p.is_active || true,
    created_at: (p as any).created_at || new Date().toISOString(),
  });

  const formatService = (s: Service) => ({
    id: s.id, title: s.name, description: s.description, price: s.price,
    category: s.category, images: [getServiceImage(s)], provider_id: s.business_id,
    business_name: s.business_name, duration: s.duration, rating: s.rating,
    delivery_mode: s.delivery_mode, is_active: s.is_featured,
    created_at: (s as any).created_at || new Date().toISOString(),
  });

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24 md:pb-8">
      <div className="max-w-5xl mx-auto px-6 md:px-8">

      {/* ── Page header ── */}
      <div className="flex items-center justify-between pt-5 pb-4">
        <div>
          <div className="flex items-center gap-1.5">
            <MapPin size={13} className="text-primary dark:text-golden" />
            <span className="text-xs font-semibold text-light-subtle dark:text-dark-subtle">
              {userArea || 'Kenya'}
            </span>
          </div>
          <h1 className="text-xl font-bold font-display text-light-text dark:text-dark-text mt-0.5">Majengo</h1>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={handleRefresh} className="p-2 rounded-full hover:bg-light-card dark:hover:bg-dark-card transition-colors">
            <RefreshCw size={18} className={`text-light-subtle dark:text-dark-subtle ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => navigate('/favorites')} className="p-2 rounded-full hover:bg-light-card dark:hover:bg-dark-card transition-colors">
            <Heart size={20} className="text-light-text dark:text-dark-text" />
          </button>
          <button onClick={() => navigate('/notifications')} className="p-2 rounded-full hover:bg-light-card dark:hover:bg-dark-card transition-colors">
            <Bell size={20} className="text-light-text dark:text-dark-text" />
          </button>
        </div>
      </div>

      {/* ── Hero ── */}
      <Hero userName={profile?.full_name} onSearch={() => navigate('/search')} />

      {/* ── Nearby banner ── */}
      <NearbyBanner
        area={userArea}
        loading={geoLoading}
        error={geoError}
        onRequest={requestLocation}
        onClear={clearLocation}
      />

      {/* ── Category pills ── */}
      <CategoryPills active={activeCategory} onChange={setActiveCategory} />

      {/* ── Error ── */}
      {error && (
        <div className="flex items-center gap-3 mb-6 px-4 py-3 rounded-xl bg-danger/10 border border-danger/20">
          <AlertCircle size={16} className="text-danger flex-shrink-0" />
          <p className="text-sm text-danger flex-1">{error}</p>
        </div>
      )}

      {/* ── Sections ── */}

      {/* 1. Properties */}
      <Section
        title={nearbyActive ? 'Properties Near You' : 'Properties in Kenya'}
        icon={<Home size={16} />}
        items={filteredVacancies}
        loading={vacanciesLoading}
        badge={nearbyActive ? 'Nearby' : undefined}
        emptyMessage={nearbyActive ? `No properties found near ${userArea}` : 'No properties available'}
        onSeeAll={() => navigate('/properties')}
        renderItem={(v: Vacancy) => (
          <PropertyCard
            id={v.id} title={v.title} location={v.location} price={v.price}
            imageUrl={getPropertyImage(v)} bedrooms={v.bedrooms} bathrooms={v.bathrooms}
            area={v.area} type={v.type} price_unit={v.price_unit}
            isFavorite={favorites.has(v.id)}
            onToggleFavorite={(id) => handleToggleFavorite(id, 'property')}
          />
        )}
      />

      {/* 2. Services */}
      <Section
        title={nearbyActive ? 'Services Near You' : 'Services in Kenya'}
        icon={<Wrench size={16} />}
        items={filteredServices}
        loading={servicesLoading}
        badge={nearbyActive ? 'Nearby' : undefined}
        emptyMessage={nearbyActive ? `No services found near ${userArea}` : 'No services available'}
        onSeeAll={() => navigate('/services')}
        renderItem={(s: Service) => (
          <ServiceCard
            service={formatService(s)}
            isFavorite={favorites.has(s.id)}
            onToggleFavorite={(id) => handleToggleFavorite(id, 'service')}
          />
        )}
      />

      {/* 3. Products */}
      <Section
        title="Popular Products"
        icon={<Zap size={16} />}
        items={featuredProducts}
        loading={productsLoading}
        emptyMessage="No products available"
        onSeeAll={() => navigate('/products')}
        renderItem={(p: Product) => (
          <ProductCard
            product={formatProduct(p)}
            isFavorite={favorites.has(p.id)}
            onToggleFavorite={(id) => handleToggleFavorite(id, 'product')}
          />
        )}
      />

      {/* 4. Short-term stays */}
      <Section
        title="Short-term Stays"
        icon={<Bed size={16} />}
        items={filterVacancies(featuredVacancies.filter(v => ['short-term', 'studio', 'villa'].includes(v.type || '')))}
        loading={vacanciesLoading}
        emptyMessage="No short-term stays available"
        onSeeAll={() => navigate('/properties?type=short-term')}
        renderItem={(v: Vacancy) => (
          <PropertyCard
            id={v.id} title={v.title} location={v.location} price={v.price}
            imageUrl={getPropertyImage(v)} bedrooms={v.bedrooms} bathrooms={v.bathrooms}
            area={v.area} type={v.type} price_unit={v.price_unit}
            isFavorite={favorites.has(v.id)}
            onToggleFavorite={(id) => handleToggleFavorite(id, 'property')}
          />
        )}
      />

      {/* 5. Majengo Recommends */}
      {featuredVacancies.length > 0 && (
        <Section
          title="Majengo Recommends"
          icon={<Star size={16} />}
          items={featuredVacancies.slice(0, 6)}
          loading={vacanciesLoading}
          emptyMessage="No recommendations available"
          renderItem={(v: Vacancy) => (
            <PropertyCard
              id={v.id} title={v.title} location={v.location} price={v.price}
              imageUrl={getPropertyImage(v)} bedrooms={v.bedrooms} bathrooms={v.bathrooms}
              area={v.area} type={v.type} price_unit={v.price_unit}
              isFavorite={favorites.has(v.id)}
              onToggleFavorite={(id) => handleToggleFavorite(id, 'property')}
            />
          )}
        />
      )}

      {/* ── Trending banner ── */}
      <div className="mb-8 rounded-2xl bg-golden/10 dark:bg-golden/5 border border-golden/20 dark:border-golden/10 p-5 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={15} className="text-golden" />
            <span className="text-xs font-bold text-golden uppercase tracking-wider">Trending Now</span>
          </div>
          <p className="text-sm font-semibold text-light-text dark:text-dark-text">Explore what's hot in your area</p>
          <p className="text-xs text-light-subtle dark:text-dark-subtle mt-0.5">Updated daily based on views & saves</p>
        </div>
        <button
          onClick={() => navigate('/feed')}
          className="flex-shrink-0 px-4 py-2 rounded-xl bg-golden text-dark-bg text-sm font-bold hover:opacity-90 transition-opacity"
        >
          View Feed
        </button>
      </div>
      </div>
    </div>
  );
};

export default HomePage;
